/**
 * TanStack Query hooks for Staff resource (StaffProfile).
 * Teachers / managers CRUD: list, get, create, update, toggle active.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type StaffRole = 'admin' | 'manager' | 'teacher';

export interface StaffProfile {
  id: string;
  userId: string;
  staffCode: string;
  branchId: string;
  branchName?: string;
  role: StaffRole;
  fullName: string;
  phone?: string | null;
  email?: string | null;
  specialization?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListStaffParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  role?: StaffRole;
  isActive?: boolean;
}

export const staffKeys = {
  all: ['staff'] as const,
  list: (params?: ListStaffParams) => ['staff', 'list', params] as const,
  detail: (id: string) => ['staff', 'detail', id] as const,
};

export function useStaffList(params?: ListStaffParams) {
  return useQuery({
    queryKey: staffKeys.list(params),
    queryFn: () =>
      api.getPage<StaffProfile>('/api/staff', params as Record<string, string>),
  });
}

export function useStaffProfile(id: string) {
  return useQuery({
    queryKey: staffKeys.detail(id),
    queryFn: () => api.get<StaffProfile>(`/api/staff/${id}`),
    enabled: !!id,
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<StaffProfile>('/api/staff', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useUpdateStaff(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<StaffProfile>(`/api/staff/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: staffKeys.all });
      void qc.invalidateQueries({ queryKey: staffKeys.detail(id) });
    },
  });
}
